import { registeredTabsHandlers } from '@/global/components/view-layouts/tabs-layout/common/registered-tabs-handlers'
import { dateService, FORMATS } from '@/global/services/date'
import { cloneDeep, getBooleanFromValue } from '@/global/utils/utils'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { api } from '@/global/services/api'
import { makeFormData } from '../../helpers/formData'
import gridView from '@/global/components/grid/GridView.vue'
import vehicleServiceIntervalGridBlock from './VehicleServiceIntervalGridBlock.vue'
import vehicleServiceIntervalMixin from '../../mixins/vehicleServiceIntervalMixin'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'

export default {
  name: 'ServiceIntervals',

  data () {
    return {
      showServiceIntervalsTable: true,
      showServiceIntervalsDetails: false,
      serviceIntervalsDetailsTitle: this.$t('fleet/vehicle_service.interval'),
      serviceIntervalsDetailsBaseTitle: this.$t('fleet/vehicle_service.interval'),
      startStateVisible: false,
      intervalVisible: false,
      startStateType: 'text',
      startStateFieldType: 'text',
      startStateSuffix: '',
      startStateSubType: '',
      startStateAppendIcon: undefined,
      startStateDateFormat: FORMATS.date,
      intervalType: 'text',
      intervalFieldType: 'text',
      intervalSuffix: '',
      archiveIntervalDialogConfig: {
        openDialog: false,
        title: this.$t('fleet/vehicle/vehicle_service_interval.archive_dialog_title'),
        positiveBtnText: this.$t('base.archive'),
        negativeBtnText: this.$t('base.cancel'),
        callbackFn: () => this.archiveServiceInterval()
      },
      archiveId: null,
      archiveFormData: {},
      archiveValidationErrors: {},
      archiveType: null,
      serviceIntervalCreateModeState: false,
      serviceIntervalValidationErrors: {},
      serviceIntervalPermissions: {}
    }
  },

  mixins: [
    vehicleServiceIntervalMixin
  ],

  computed: {
    serviceIntervalsConfig () {
      return {
        title: this.$t('fleet/vehicle.service_intervals'),
        key: 'serviceIntervals',
        visible: getBooleanFromValue(this.permissions?.canReadVehicleServiceInterval),
        validationErrors: this.serviceIntervalValidationErrors,
        onTabChange: () => {
          this.showMainEditButtons = false
        },
        createModeUpdate: (event) => {
          const { createMode } = event || {}
          this.serviceIntervalCreateModeState = !!createMode
        },
        tabContent: [
          // table view
          {
            component: this.tableGridComponent,
            visible: this.showServiceIntervalsTable,
            apiConfig: {
              get: {
                module: 'fleet',
                route: 'vehicle-service',
                queryParams: {
                  vehicle_id: this.$route.params.id
                }
              }
            },
            rowDblClick: (event) => this.onShowServiceIntervalDetails(event),
            mobileConfig: {
              title: this.$t('fleet/vehicle.service_intervals')
            },
            title_and_back_config: {
              title: this.$t('fleet/vehicle.service_intervals')
            },
            search_and_buttons_config: {
              newClick: () => this.onNewServiceInterval(),
              showNewBtn: getBooleanFromValue(this.serviceIntervalPermissions?.canCreate)
            },
            gridConfig: {
              component: gridView,
              blockConfig: {
                component: vehicleServiceIntervalGridBlock,
                showItem: this.showItem,
                showDetails: () => {
                  this.showServiceIntervalsTable = false
                  this.showServiceIntervalsDetails = true
                },
                setDetailsTitle: (val) => {
                  this.serviceIntervalsDetailsTitle = val
                },
                configDynamicFields: (event) => {
                  const { name } = event?.data?.type || {}
                  if (name) {
                    this.configDynamicFields(name)
                  }
                }
              },
              useBorder: false
            },
            onDataReceivedWithParams: (event) => {
              this.serviceIntervalPermissions = event?.data?.meta?.permissions || {}
            },
            tableConfig: {
              cellsConfig: {
                type: {
                  custom_display_value: (item) => {
                    return item?.type?.description ? this.$t(item.type.description) : ''
                  }
                },
                start_state: {
                  custom_display_value: (item) => {
                    return (item?.type?.name === 'km' || item?.type?.name === 'hours') && item?.start_state
                      ? item.start_state + ' ' + (item?.unit || '')
                      : dateService.formatFromUtc(item.start_state, FORMATS.date)
                  }
                },
                interval: {
                  custom_display_value: (item) => {
                    return (item?.interval || '') + ' ' + (item?.unit || '')
                  }
                },
                current_state: {
                  custom_display_value: (item) => {
                    return (item?.type?.name === 'km' || item?.type?.name === 'hours')
                      ? this.showItem?.item?.odometer || ''
                      : dateService.formatFromUtc(item.current_state, FORMATS.date)
                  }
                },
                until_next_service: {
                  custom_display_value: (item) => {
                    return (item?.type?.name === 'km' || item?.type?.name === 'hours') && item?.start_state && item?.interval && this.showItem?.item?.odometer_value
                      ? (item.start_state + item.interval - this.showItem.item.odometer_value) + ' ' + (item?.unit || '')
                      : (item?.until_next_service || '') + ' ' + (item?.unit && item?.until_next_service ? item.unit : '')
                  }
                },
                upcoming_service: {
                  custom_display_value: (item) => {
                    return (item?.type?.name === 'km' || item?.type?.name === 'hours') && item?.start_state && item?.interval
                      ? (item.start_state + item.interval) + ' ' + (item?.unit || '')
                      : dateService.formatFromUtc(item.upcoming_service, FORMATS.date)
                  }
                }
              },
              headers: [
                {
                  label: this.$t('fleet/vehicle_service.service_type'),
                  key: 'type',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_service.name'),
                  key: 'name',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_service.start_state'),
                  key: 'start_state',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_service.interval'),
                  key: 'interval',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_service.current_state'),
                  key: 'current_state',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_service.until_next_service'),
                  key: 'until_next_service',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_service.upcoming_service'),
                  key: 'upcoming_service',
                  always_visible: true
                }
              ],
              actionsConfig: [
                showDetailsAction(this.$t, (event) => this.onShowServiceIntervalDetails(event), { visible: getBooleanFromValue(this.serviceIntervalPermissions?.canRead) }),
                {
                  label: this.$t('fleet/vehicle_service.archive_service'),
                  src: '/img/icons/report-icon.svg',
                  click: (event) => this.onArchiveServiceInterval(event)
                },
                deleteAction(this.$t, (event) => this.onIntervalDelete(event), { visible: getBooleanFromValue(this.serviceIntervalPermissions?.canDelete) })
              ],
              paginationConfig: {
                perPageOptions: [50, 100]
              },
              filtersConfig: {
                fields: [
                  {
                    key: 'type',
                    label: this.$t('fleet/vehicle_service.service_type'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: this.showItem?.item?.is_working_machine ? 'vehicle-service/working-machine-service-types' : 'vehicle-service/base-service-types',
                      custom_display_value: (item) => {
                        return item?.description ? this.$t(item.description) : ''
                      },
                      returnValue: 'object'
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'name',
                    label: this.$t('fleet/vehicle_service.name'),
                    visible: true,
                    editable: true
                  }
                ]
              }
            }
          },
          // details view
          {
            component: this.tabActionsComponent,
            visible: this.showServiceIntervalsDetails,
            title: this.serviceIntervalsDetailsTitle,
            showEditButtons: true,
            showEditBtn: getBooleanFromValue(this.serviceIntervalPermissions?.canUpdate),
            showBackButton: {
              onGoBack: () => this.exitFromServiceIntervalDetails()
            },
            onCancelTabSave: () => this.exitFromServiceIntervalDetails(true),
            onSaveClick: () => this.onSaveServiceInterval()
          },
          {
            component: this.columnsContentComponent,
            visible: this.showServiceIntervalsDetails,
            showBackButton: {
              useChangesNotSavedDialog: false
            },
            rows: [
              {
                disabled: !getBooleanFromValue(this.serviceIntervalPermissions?.canUpdate),
                columnsCount: 2,
                fields: [
                  {
                    key: 'type',
                    label: this.$t('fleet/vehicle_service.service_type'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: this.showItem?.item?.is_working_machine ? 'vehicle-service/working-machine-service-types' : 'vehicle-service/base-service-types',
                      custom_display_value: (item) => {
                        return item?.description ? this.$t(item.description) : ''
                      },
                      returnValue: 'object'
                    },
                    onSelect: (event) => this.onTypeSelection(event),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'name',
                    label: this.$t('fleet/vehicle_service.name'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'start_state',
                    label: this.$t('fleet/vehicle_service.start_state'),
                    type: this.startStateType,
                    field_type: this.startStateFieldType,
                    onInput: (value, event) => {
                      if (this.startStateType === 'datetime-picker') {
                        const { updateValidationErrors } = event || {}

                        if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                          updateValidationErrors({
                            start_state: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                          })
                        }
                      }
                    },
                    suffix: this.startStateSuffix,
                    appendIcon: this.startStateAppendIcon,
                    subType: this.startStateSubType,
                    format: this.startStateDateFormat,
                    visible: this.startStateVisible,
                    editable: true
                  },
                  {
                    key: 'interval',
                    label: this.$t('fleet/vehicle_service.interval'),
                    type: this.intervalType,
                    field_type: this.intervalFieldType,
                    suffix: this.intervalSuffix,
                    visible: this.intervalVisible,
                    editable: true
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  methods: {
    onNewServiceInterval () {
      const { setCreateMode, clearData, updateData } = registeredTabsHandlers?.serviceIntervals || {}
      if (typeof setCreateMode === 'function') {
        setCreateMode(true)
      }
      if (typeof clearData === 'function') {
        clearData()
      }
      this.serviceIntervalsDetailsTitle = this.$t('fleet/vehicle.new_vehicle_service_interval')

      if (typeof updateData === 'function') {
        updateData({
          start_state: null,
          interval: null
        })
      }

      this.startStateVisible = false
      this.intervalVisible = false
      this.showServiceIntervalsTable = false
      this.showServiceIntervalsDetails = true
    },

    exitFromServiceIntervalDetails (canceling = false) {
      const { clearData, setCreateMode, setEditMode, getCreateModeState, cancelSave, getFormData } = registeredTabsHandlers?.serviceIntervals || {}
      let isCreateMode = false
      if (typeof getCreateModeState === 'function') {
        isCreateMode = getCreateModeState()
      }

      if (isCreateMode) {
        this.showServiceIntervalsTable = true
        this.showServiceIntervalsDetails = false

        if (typeof clearData === 'function') {
          clearData()
        }
        if (typeof setCreateMode === 'function') {
          setCreateMode(false)
        }
        if (typeof setEditMode === 'function') {
          setEditMode(false)
        }
      }
      else if (typeof cancelSave === 'function') {
        if (!canceling) {
          this.showServiceIntervalsTable = true
          this.showServiceIntervalsDetails = false
        }

        cancelSave()

        if (typeof getFormData === 'function') {
          const data = getFormData()
          const { name } = data?.data?.type || {}
          if (name) {
            this.configDynamicFields(name)
          }
        }
      }
      this.serviceIntervalValidationErrors = {}
    },

    async onSaveServiceInterval () {
      try {
        const { getFormData } = registeredTabsHandlers?.serviceIntervals || {}
        const { fetchData } = registeredTableHandlers?.serviceIntervals || {}
        const { id: vehicleId } = this.$route?.params || {}

        if (!vehicleId) return

        const data = typeof getFormData === 'function' ? getFormData()?.data : {}
        const datesTransform = {}

        if (data?.type?.name === 'date') {
          datesTransform.start_state = {
            fromFormat: FORMATS.date,
            toFormat: FORMATS.isoDate
          }
        }
        const formData = makeFormData(data,
          [],
          {
            vehicle_id: vehicleId
          },
          datesTransform
        )

        let apiUrl = 'vehicle-service'
        const { id: rangeLimitId } = data || {}
        if (!this.serviceIntervalCreateModeState && rangeLimitId) {
          formData.append('_method', 'PATCH')
          apiUrl += `/${rangeLimitId}`
        }

        await api('fleet').post(apiUrl, formData)
        this.exitFromServiceIntervalDetails()

        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        this.serviceIntervalValidationErrors = exception?.response?.data?.errors || {}
        console.error(exception)
      }
    },

    onTypeSelection (event) {
      const { name } = event?.selectedItem || {}
      const { updateData } = registeredTabsHandlers?.serviceIntervals || {}
      if (typeof updateData === 'function') {
        updateData({
          start_state: null,
          interval: null
        })
      }

      if (!name) {
        this.startStateVisible = false
        this.intervalVisible = false
        return
      }

      this.configDynamicFields(name)
    },

    configDynamicFields (name) {
      switch (name) {
        case 'km':
          this.startStateType = 'text'
          this.startStateFieldType = 'number'
          this.startStateSuffix = 'km'
          this.startStateAppendIcon = undefined
          this.startStateVisible = true

          this.intervalType = 'text'
          this.intervalFieldType = 'number'
          this.intervalSuffix = 'km'
          this.intervalVisible = true
          break
        case 'hours':
          this.startStateType = 'text'
          this.startStateFieldType = 'number'
          this.startStateSuffix = 'h'
          this.startStateVisible = true

          this.intervalType = 'text'
          this.intervalFieldType = 'number'
          this.intervalSuffix = 'h'
          this.intervalVisible = true
          break
        case 'date':
          this.startStateType = 'datetime-picker'
          this.startStateSubType = 'date'
          this.startStateFieldType = 'text'
          this.startStateSuffix = undefined
          this.startStateDateFormat = FORMATS.date
          this.startStateAppendIcon = 'mdi-calendar-month'
          this.startStateVisible = true

          this.intervalType = 'text'
          this.intervalSuffix = this.$t('fleet/vehicle.day_s')
          this.intervalVisible = true
      }
    },

    onIntervalArchiveDataUpdate (data) {
      this.archiveFormData = cloneDeep(data)
    }
  }
}
